import '../scss/components/aps-location-search.scss';
import {
    getSearchParams,
    updateGeoSearchUrl
} from './utils/search-params.js';
import {round} from "./utils/round.js";
import {setAlert} from "./utils/alert.js";
import {Loader} from "@googlemaps/js-api-loader"
import {MarkerClusterer, GridAlgorithm} from "@googlemaps/markerclusterer";
import {createFacetFilter, removeFilters} from "./utils/filter.js";
import {markerSvg, pinMarker, clusterIcon, batchMarker, defaultMarker} from "./utils/marker-cluster.js";
import {createMarkerEntry, createMarkerEntries, setViewActive} from "./utils/marker-info-views.js";
import {parseRF} from "./utils/parseSerializeRF.js";

const locSearchEl = document.querySelector('.location-search');
const loader = new Loader({
    apiKey: 'AIzaSyDa3CGEqCfEdw4P0UvHT7EDwTbF44HOBco',
    version: 'weekly',
    language: 'de',
    region: 'DE',
    libraries: ['places']
});

let locSearchResults = {};
let searchConfig = {
    p: null,
    lat: '50.937531',
    long: '6.960279',
    zoom: 13,
    bbox: null,
    m: null,
    isBatchMarker: false,
    selectedMarker: null,
    batchMarkerLimit: null,
    rf: '',
    parsedRf: '',
    e: null
};
let isLoading = false;
let map = null;
let markerCluster = null;
let markers = [];
let activeMarker = null;

if (locSearchEl) {
    const sidebarEl = locSearchEl.querySelector('.location-search_aside');
    const mapEl = locSearchEl.querySelector('.location-search_map');

    createPageLoadingSpinner();
    parseQueryParameter(getSearchParams());

    loader.load().then(() => {
        initGoogleMap();
        initGoogleAutocomplete();

        if(searchConfig.p == null) useGeoLocation();
    }).then(() => {
        mapMoveAddEventListener();
        removePageLoadingSpinner();
    });

    document.addEventListener('apsLocationSearchFilter', async function (e) {
        if (e.detail.updated) {
            createPageLoadingSpinner();
            disableFilterButtons();
            try {
                parseQueryParameter(getSearchParams());

                locSearchResults = await fetchApsData(`/api/aps/geo-search/map-marker`, 'POST', {
                    bbox: searchConfig.bbox,
                    requiredfields: searchConfig.rf
                });

                const apsFacets = locSearchResults.facets;
                const apsMarkers = locSearchResults.markers;

                if (apsFacets) createFilters(apsFacets);
                if (apsMarkers) createMarkers(apsMarkers);
            } catch (error) {
                console.error('Fehler beim laden der Map Daten', error);
            } finally {
                removePageLoadingSpinner();
                enableFilterButtons();
            }
        }
    });

    sidebarEl.querySelectorAll('.location-search_aside-back-btn button').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const type = e.currentTarget.dataset.type;
            const columnEl = sidebarEl.querySelector('.location-search_aside-column');
            setViewActive(type, 'remove', columnEl);
            if (type === 'list') {
                changeMarkerIcon(activeMarker, true);
                setTimeout(() => {
                    resetInnerHtml(sidebarEl.querySelector('.location-search_aside-list-items'));
                }, 500);
            } else if (type === 'detail') {
                if (!columnEl.classList.contains('list-view')) {
                    changeMarkerIcon(activeMarker, true);
                }
                setTimeout(() => {
                    resetInnerHtml(sidebarEl.querySelector('.location-search_aside-detail-view'));
                }, 500);
            }
        });
    });

    sidebarEl.querySelector('.location-search_autocomplete_icon-right').addEventListener('click', async (event) => {
        event.preventDefault();
        await useGeoLocation();
    });

    function initGoogleMap() {
        map = new google.maps.Map(mapEl, {
            center: new google.maps.LatLng(searchConfig.lat, searchConfig.long),
            zoom: searchConfig.zoom,
            mapId: '1f9e1832918b226d',
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: false,
        });
    }

    function initGoogleAutocomplete() {
        const options = {
            fields: ['formatted_address', 'geometry', 'name'],
            strictBounds: false,
        };
        const input = sidebarEl.querySelector('.location-search_autocomplete_combobox_input');
        const autocomplete = new google.maps.places.Autocomplete(input, options);

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();

            if (!place.geometry || !place.geometry.location) {
                console.error('No details available for input: "' + place.name + '"');
                return;
            }
            searchConfig.lat = place.geometry.location.lat();
            searchConfig.long = place.geometry.location.lng();

            if (place.geometry.viewport) {
                map.fitBounds(place.geometry.viewport);
            } else {
                map.setCenter(place.geometry.location);
                map.setZoom(17);
            }
        });
    }

    function preventClick(event) {
        event.preventDefault();
        event.stopPropagation();
    }

    function disableFilterButtons() {
        const facetItems = document.querySelectorAll('.filter-facets_facet-item');

        facetItems.forEach((item) => {
            item.classList.add('disabled');
            item.addEventListener('click', preventClick, true);
        });
    }

    function enableFilterButtons() {
        const facetItems = document.querySelectorAll('.filter-facets_facet-item');

        facetItems.forEach((item) => {
            item.classList.remove('disabled');
            item.removeEventListener('click', preventClick, true);
        });
    }

    function createPageLoadingSpinner() {
        isLoading = true;

        const pageSpinner = document.createElement('div');
        pageSpinner.classList.add('location-search_page-spinner');
        pageSpinner.innerHTML = `
            <div class="location-search_page-spinner_inner">
                <svg viewBox="0 0 50 50">
                    <circle cx="25" cy="25" r="20"></circle>
                </svg>
                <span>Lade Daten</span>
            </div>
            <div class="location-search_page-spinner_bg"></div>
        `;

        mapEl.closest('.location-search_map-container').appendChild(pageSpinner);
    }

    function removePageLoadingSpinner() {
        isLoading = false;
        const pageSpinner = locSearchEl.querySelector('.location-search_page-spinner');
        if (pageSpinner) pageSpinner.remove();
    }

    function parseQueryParameter(query) {
        if (!query) return null;

        searchConfig.p = query.get('p') || null;
        if (searchConfig.p != null) {
            const [lat, lng, zoom] = searchConfig.p.split(',').map(v => {
                if (v.startsWith('z')) {
                    return parseInt(v.slice(1));
                }
                return parseFloat(v);
            });
            searchConfig.lat = lat;
            searchConfig.long = lng;
            searchConfig.zoom = zoom;
        }

        searchConfig.m = query.get('m') || null;
        if (searchConfig.m != null) {
            if (searchConfig.m.startsWith('ba__')) {
                let [markerId, limit] = searchConfig.m.slice(4).split('__l');
                limit = limit ? parseInt(limit, 10) : null;
                searchConfig.isBatchMarker = true;
                searchConfig.selectedMarker = markerId;
                searchConfig.batchMarkerLimit = limit;
            } else {
                searchConfig.isBatchMarker = false;
                searchConfig.selectedMarker = query.m;
            }
        } else {
            searchConfig.isBatchMarker = false;
            searchConfig.selectedMarker = null;
        }

        searchConfig.rf = query.get('requiredfields') || '';
        if (searchConfig.rf != null) {
            searchConfig.parsedRf = parseRF(searchConfig.rf);
        } else {
            searchConfig.parsedRf = '';
        }

        searchConfig.e = query.get('e') || null;
    }

    function useGeoLocation() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(successCallback, errorCallback);
        }
    }

    function successCallback(position) {
        searchConfig.lat = round(position?.coords?.latitude, 6);
        searchConfig.long = round(position?.coords?.longitude, 6);

        if(map != null) map.setCenter({lat: searchConfig.lat, lng: searchConfig.long});

        sidebarEl.querySelector('.location-search_autocomplete_geo-on-icon').classList.add('has-geo');
        sidebarEl.querySelector('.location-search_autocomplete_geo-on-icon path').setAttribute('d', 'M12 8a4 4 0 100 8 4 4 0 000-8zm8.94 3A9 9 0 0013 3.06V1h-2v2.06A9 9 0 003.06 11H1v2h2.06A9 9 0 0011 20.94V23h2v-2.06A9 9 0 0020.94 13H23v-2h-2.06zM12 19a7 7 0 11-.01-13.99A7 7 0 0112 19z');
    }

    function errorCallback(error) {
        console.error('Error Geolocation', error);
        setAlert('error', 'Standort konnte nicht bestimmt werden.');
        if (sidebarEl.querySelector('.location-search_autocomplete_geo-on-icon').classList.contains('has-geo')) {
            sidebarEl.querySelector('.location-search_autocomplete_geo-on-icon').classList.remove('has-geo');
        }
    }

    async function fetchApsData(url, method = 'GET', data) {
        try {
            let response;

            if (data == null) {
                response = await fetch(url, {method: method, credentials: 'include'})
            } else {
                response = await fetch(url, {
                    method: method,
                    credentials: 'include',
                    body: JSON.stringify(data)
                })
            }
            return await response.json();
        } catch (error) {
            //console.error("Error:", error);
            return {error: true, message: error.message};
        }
    }

    function createFilters(response) {
        const elementClass = '.location-search_filter-facets';
        resetInnerHtml(sidebarEl.querySelector(elementClass));
        const facets = splitFacets(response, searchConfig.rf);
        createFacetFilter(facets, elementClass, 'apsLocationSearchFilter');
    }

    function splitFacets(responseFacets, requiredfields) {
        const requiredfieldsObject = parseRequiredFields(requiredfields);
        const newFacets = [];

        if (!responseFacets) return newFacets;

        const noFacets = sidebarEl.querySelector('.filter-facets_no-facets');
        if (noFacets) noFacets.remove();

        responseFacets.forEach(facet => {
            if (facet.entries && facet.entries.length) {
                const filtered = {
                    selectedEntries: [],
                    otherEntries: [],
                    moreNum: 0,
                };

                for (const entry of facet.entries) {
                    if (requiredfieldsObject && requiredfieldsObject[facet.id]) {
                        const isValueIncluded = requiredfieldsObject[facet.id].some(value => {
                            return String(entry.value) === String(value);
                        });
                        if (isValueIncluded) {
                            filtered.selectedEntries.push(entry);
                        } else {
                            filtered.otherEntries.push(entry);
                            filtered.moreNum += 1;
                        }
                    } else {
                        filtered.otherEntries.push(entry);
                        filtered.moreNum += 1;
                    }
                }

                newFacets.push({
                    ...facet,
                    ...filtered,
                });
            }
        });

        return newFacets;
    }

    function parseRequiredFields(inputString) {
        if (!inputString) return null;
        const resultObject = {};
        const entries = inputString.split('|');

        entries.forEach((entry) => {
            const pairs = entry.split(/"\."/);

            pairs.forEach(pair => {
                const escapedPair = pair.replace(/"/g, '');
                const [field, value] = escapedPair.split(':');
                const unescapedField = decodeURIComponent(field.trim());
                const unescapedValue = decodeURIComponent(value.trim());

                if (!resultObject[unescapedField]) {
                    resultObject[unescapedField] = [];
                }

                resultObject[unescapedField].push(unescapedValue);
            });
        });

        return resultObject;
    }

    function mapMoveAddEventListener() {
        if (map == null) return;

        map.addListener('idle', () => {
            const center = map.getCenter();
            const bounds = map.getBounds();
            const bbox = {
                ne: {
                    lat: bounds.getNorthEast().lat(),
                    lng: bounds.getNorthEast().lng(),
                },
                sw: {
                    lat: bounds.getSouthWest().lat(),
                    lng: bounds.getSouthWest().lng(),
                },
            };

            searchConfig.zoom = map.getZoom();
            searchConfig.lat = round(center.lat(), 6);
            searchConfig.long = round(center.lng(), 6);

            searchConfig.bbox = btoa(JSON.stringify(bbox));

            updateGeoSearchUrl(searchConfig, 'apsLocationSearchFilter');
        });
    }

    function createMarkers(apsMarkers) {
        if (map == null || apsMarkers == null) return;
        if (markers.length > 0 && markerCluster != null) resetMapMarkers();
        createUserPositionMarker();

        const mappedMarkers = apsMarkers.map((item) => {
            if (item.lat != null || item.lng != null) {
                const entryCount = item.entries.length;
                const isBatch = entryCount > 1;
                const labelText = entryCount > 99 ? '99+' : `${entryCount}`;
                const labelSize = entryCount > 99 ? '7px' : '9px';
                const label = {
                    text: labelText,
                    color: 'white',
                    fontSize: labelSize,
                };

                const pin = new google.maps.Marker({
                    position: new google.maps.LatLng(item.lat, item.lng),
                    ...(isBatch ? {label} : {}),
                    icon: {
                        anchor: isBatch ? {x: 18, y: 18} : {x: 15, y: 15},
                        url: isBatch ? batchMarker : defaultMarker,
                        labelOrigin: {x: 27, y: 28},
                        scaledSize: isBatch ? {width: 36, height: 36} : {width: 30, height: 30},
                    },
                });

                pin.__isBatch = isBatch;
                pin.__id = item.id;

                pin.addListener('click', (/*{domEvent, latLng}*/) => {
                    if (!activeMarker || activeMarker.__id !== pin.__id) {
                        const url = getSearchParams();
                        searchConfig.m = isBatch ? `ba__${item.id}` : item.id;
                        const elements = {
                            column: sidebarEl.querySelector('.location-search_aside-column'),
                            list: sidebarEl.querySelector('.location-search_aside-list-items'),
                            detail: sidebarEl.querySelector('.location-search_aside-detail-view')
                        }

                        if (url.has('e')) url.delete('e');
                        url.set('m', searchConfig.m);
                        window.history.replaceState(null, null, `?${url.toString()}`);

                        changeMarkerIcon(pin);

                        fetchAndDisplayEntries(item.id).then(data => {
                            resetInnerHtml(elements.list);
                            resetInnerHtml(elements.detail);

                            if (data.count > 1) {
                                createMarkerEntries(data.entries, elements);
                                pin.__type = 'list';
                            } else {
                                createMarkerEntry(data.entries[0], elements);
                                pin.__type = 'detail';
                            }
                        });
                    } else if (activeMarker.__id === pin.__id) {
                        sidebarEl.querySelector(`.location-search_aside-${activeMarker.__type} .location-search_aside-back-btn button`).click();
                    }
                });

                return pin;
            }
        });

        markers = [...mappedMarkers];

        let renderer = {
            render: ({count, position}) => {
                const size = count > 9 ? count > 99 ? 45 : 40 : 35;
                const fontSize = count > 9 ? count > 99 ? '12px' : '11px' : '10px';
                const svg = window.btoa(`
                  <svg fill="rgb(113, 113, 113)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
                    <circle cx="120" cy="120" r="70" stroke="white" stroke-width="2" />    
                  </svg>`
                );
                return new google.maps.Marker({
                    position,
                    icon: {
                        url: clusterIcon({w: size, h: size}),
                        scaledSize: new google.maps.Size(size, size),
                    },
                    label: {
                        text: String(count),
                        color: "white",
                        fontSize: fontSize,
                    },
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                });
            }
        }
        let algorithm = new GridAlgorithm({gridSize: 48})
        let config = {map, markers, renderer, algorithm};

        markerCluster = new MarkerClusterer(config);
    }

    function createUserPositionMarker() {
        if (map == null) return;

        const userMarker = new google.maps.Marker({
            position: new google.maps.LatLng(searchConfig.lat, searchConfig.long),
            icon: {
                anchor: new google.maps.Point(15, 25),
                url: markerSvg,
                scaledSize: {width: 30, height: 30},
            },
            map: map
        });
        markers.push(userMarker);
    }

    function resetMapMarkers() {
        markerCluster.removeMarkers(markers);
        for (let i = 0; i < markers.length; i++) {
            markers[i].setMap(null);
            //google.maps.event.clearInstanceListeners(markers[i]);
        }
        markers = [];
    }

    async function fetchAndDisplayEntries(entryId) {
        return await fetchApsData(`/api/aps/geo-search/marker-entries`, 'POST', {
            'id': entryId,
            'limit': 50,
            'orderBy': "title",
            'requiredfields': searchConfig.rf
        });
    }

    function changeMarkerIcon(pin, resetActiveMarker = false) {
        if (activeMarker) {
            activeMarker.setIcon(activeMarker.oldMarker);
            activeMarker.setLabel(activeMarker.oldLabel);
        }
        if (!resetActiveMarker) {
            activeMarker = pin;
            activeMarker.oldMarker = pin.getIcon();
            activeMarker.oldLabel = pin.getLabel();

            pin.setIcon({
                anchor: {x: 15, y: 20},
                url: pinMarker,
            });
            pin.setLabel(null);
        } else {
            activeMarker = null;
        }
    }

    function resetInnerHtml(element) {
        if (!element) return;
        element.innerHTML = '';
    }
}
